'use client';

import { useState } from 'react';
import Link from 'next/link';
import Arrow from './Arrow';
import { FACEBOOK_URL } from '@/lib/homepage/links';

/** Green flood at the end: get draw-night reminders by email, or follow on Facebook. */
export default function ReminderBand() {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState({ status: 'idle', message: '' });

  const submit = async (e) => {
    e.preventDefault();
    setState({ status: 'sending', message: '' });
    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, consent }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setState({ status: 'done', message: 'You’re on the list. Check your inbox.' });
      } else {
        setState({ status: 'error', message: data.error || 'Something went wrong. Please try again.' });
      }
    } catch {
      setState({ status: 'error', message: 'Something went wrong. Please try again.' });
    }
  };

  return (
    <section className="bg-accent text-accent-foreground" aria-labelledby="remind-h">
      <div className="max-w-[1520px] mx-auto px-[var(--gutter)] py-[clamp(3.5rem,7vw,6rem)] grid grid-cols-12 gap-x-6 gap-y-8 items-end">
        <div className="col-span-12 lg:col-span-6">
          <h2 id="remind-h" className="d d-italic text-[length:var(--t-loud)] -ml-[0.03em]">
            Never miss a draw night.
          </h2>
          <p className="mt-5 max-w-[38ch] text-[length:var(--t-lede)] leading-[1.4] font-medium">
            An email on draw day with what&apos;s being drawn and when, plus new competitions as they open.
          </p>
        </div>

        <div className="col-span-12 lg:col-start-8 lg:col-span-5">
          {state.status === 'done' ? (
            <p className="d text-[length:var(--t-h3)]" role="status">
              {state.message}
            </p>
          ) : (
            <form onSubmit={submit} noValidate>
              <label htmlFor="remind-email" className="block font-semibold mb-2">
                Your email
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  id="remind-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="min-w-0 flex-1 bg-white text-black placeholder:text-black/45 border-2 border-black px-4 py-3.5 text-base focus:outline-none focus:ring-4 focus:ring-black/25"
                  placeholder="you@example.com"
                />
                <button type="submit" disabled={state.status === 'sending'} className="btn-block btn-ink disabled:opacity-70">
                  {state.status === 'sending' ? 'Adding you…' : 'Remind me'} <Arrow />
                </button>
              </div>
              <label className="mt-4 flex items-start gap-3 text-[0.95rem] font-medium leading-snug cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-5 w-5 shrink-0 accent-black"
                />
                <span>
                  Yes, email me draw-night reminders and new competitions. I can unsubscribe at any time. See our{' '}
                  <Link href="/privacy-policy" className="underline underline-offset-2">
                    privacy policy
                  </Link>
                  .
                </span>
              </label>
              {state.status === 'error' && (
                <p className="mt-3 font-semibold" role="alert">
                  {state.message}
                </p>
              )}
            </form>
          )}
          <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="link-more mt-6 font-semibold"
          >
            Or follow us on Facebook <Arrow />
          </a>
        </div>
      </div>
    </section>
  );
}
